/**
 * Created by noOne on 19/02/2018.
 */
"use strict";

/**
 * @class Class that uses Perlin noise to provide a camera shake effect (will shake any display object, use on world container for camera shake)
 * @author Owain Bell - PlayerThree - 2018
 */
export default class Shake {
    constructor() {

        /**
         * Perlin noise generator
         * @member {SimplexNoise}
         * @private
         */
        this._simplex = new SimplexNoise();

        /**
         * Trauma level, increase to add to trauma
         * @member {number}
         * @private
         */
        this._trauma = 0;

        /**
         * Should shake include rotation?
         * @member{boolean}
         * @private
         */
        this._useRotation = false;

        /**
         * The display object we want to shake
         * @member {null|PIXI.Point|PIXI.DisplayObject}
         * @private
         */
        this._shakeTarget = null;

        /**
         * Position of object before shake, use to reset position each frame and at end of animation.
         * Set when setting the trauma level when the target isn't already shaking
         * @member {null|PIXI.Point}
         * @private
         */
        this._shakeStart = null;

        /**
         * @deprecated
         * @member {number}
         * @private
         */
        this._shakeTime = -1;

        /**
         * How quickly should the trauma fade, higher levels result in quicker fade
         * @member {number}
         * @private
         */
        this._traumaFade = 0.5;

        /**
         * Max X offset for shake.  Increase to turn up shake violence.
         * @member {number}
         * @private
         */
        this._maxOffsetX = 20;

        /**
         * Max Y offset for shake.  Increase to turn up shake violence.
         * @member {number}
         * @private
         */
        this._maxOffsetY = 20;

        /**
         * Max rotational offset for shake.  Increase to turn up shake violence.
         * @member {number}
         * @private
         */
        this._maxRot = Math.PI * 0.02;

        /**
         * Is shaking flag
         * @member {boolean}
         * @private
         */
        this._shaking = false;

        /**
         * Random seed
         * @member {*|number|BigNumber}
         * @private
         */
        this._seed = Math.random();

        /**
         * Step is incremented each frame, for our stepped procedural noise generator
         * @member {number}
         * @private
         */
        this._step = 0;

        /**
         * Rotational centre point
         * @member {PIXI.Point}
         * @private
         */
        this._centre = null;

        /**
         * Optional callback function for when shake ends
         * @member {null|function}
         * @private
         */
        this._onShakeEnd = null;

    }

    update(dt) {

        if (this._useRotation && this._centre) {
            this._shakeTarget.pivot.set(
                this._centre.x + (this._shakeTarget.x - Common.STAGE_WIDTH * 0.5),
                this._centre.y + (this._shakeTarget.y - Common.STAGE_HEIGHT * 0.5)
            );
        }

        if (!this._shakeTarget || !this._shaking) return;

        this._resetTarget();

        const offsetX = this._trauma * this._maxOffsetX * this._simplex.noise(this._seed, this._step);
        const offsetY = this._trauma * this._maxOffsetY * this._simplex.noise(this._seed + 1, this._step);

        this._shakeTarget.x += offsetX;
        this._shakeTarget.y += offsetY;

        if (this._useRotation) {
            const offsetR = this._trauma * this._maxRot * this._simplex.noise(this._seed + 2, this._step);
            this._shakeTarget.rotation += offsetR;
        }

        this._trauma -= dt * this._traumaFade;
        this._step++;

        if (this._trauma < 0) {
            this._end();
        }
    }

    setTarget(target) {
        target = target || this._shakeTarget;
        this._shakeTarget = target;
        this._shakeStart = new PIXI.Point(target.x, target.y);
        this._shakeStartRot = target.rotation;
        this._step = 0;
    }

    setRotCentre(point) {
        this._centre = point;
    }

    _end() {
        this._resetTarget();
        this._shaking = false;
        if (this._onShakeEnd) this._onShakeEnd();//cb
    }

    _resetTarget() {
        this._shakeTarget.position.set(this._shakeStart.x, this._shakeStart.y);
        this._shakeTarget.rotation = this._shakeStartRot;
    }

    get trauma() {
        return this._trauma;
    }

    set trauma(value) {

        //todo - should we make new start when trauma is incremented?
        if (this._shakeTarget && !this._shaking) {
            this._shakeStart = new PIXI.Point(this._shakeTarget.x, this._shakeTarget.y);
        }

        this._shaking = true;
        this._trauma = value;

        //capped at 0-1 range
        if (this._trauma > 1) {
            this._trauma = 1;
        }
    }

    get useRotation() {
        return this._useRotation;
    }

    set useRotation(value) {
        this._useRotation = value;
    }

    get maxOffsetX() {
        return this._maxOffsetX;
    }

    set maxOffsetX(value) {
        this._maxOffsetX = value;
    }

    get maxOffsetY() {
        return this._maxOffsetY;
    }

    set maxOffsetY(value) {
        this._maxOffsetY = value;
    }

    get maxRot() {
        return this._maxRot;
    }

    set maxRot(value) {
        this._maxRot = value;
    }

    get traumaFade() {
        return this._traumaFade;
    }

    set traumaFade(value) {
        this._traumaFade = value;
    }

    set onShakeEnd(value) {
        this._onShakeEnd = value;
    }
}

//-- converted to javascript from java code found in this paper -- http://staffwww.itn.liu.se/~stegu/simplexnoise/simplexnoise.pdf --//
/**
 * @class - Perlin noise generator, generates stepped procedural noise, more ordered and pleasing than pure randomness.
 */
class SimplexNoise {
    constructor() {

        this._grad3 = [[1, 1, 0], [-1, 1, 0], [1, -1, 0], [-1, -1, 0], [1, 0, 1], [-1, 0, 1], [1, 0, -1], [-1, 0, -1], [0, 1, 1], [0, -1, 1], [0, 1, -1], [0, -1, -1]];

        this._p = new Array(256);

        // To remove the need for index wrapping, double the permutation table length
        this._perm = new Array(512);

        this.init();
    }

    init() {

        for (let i = 0; i < this._p.length; i++) {
            this._p[i] = Math.floor(Math.random() * 256);//fill with random bit strings
        }

        for (let i = 0; i < this._perm.length; i++) {
            this._perm[i] = this._p[i & 255];
        }
    }

    noise(xIn, yIn) {
        // 2D simplex noise

        let n0, n1, n2;//noise contributions from three corners

        // Skew the input space to determine which simplex cell we're in
        const f2 = 0.5 * (Math.sqrt(3) - 1);
        let s = (xIn + yIn) * f2;
        let i = Math.floor(xIn + s);
        let j = Math.floor(yIn + s);

        const g2 = (3 - Math.sqrt(3)) / 6;
        let t = (i + j) * g2;
        let X0 = i - t; //Unskew cell origin back to (x, y) space
        let Y0 = j - t; //^
        let x0 = xIn - X0; //distances from cell origin
        let y0 = yIn - Y0; //^

        //Note - for 2d, the simplex shape is a equilateral triangle
        //determine which simplex we are
        let i1, j1;//offsets for 2nd (middle) corner of simplex in (i,j) coords

        if (x0 > y0) { // lower triangle, XY order: (0,0)->(1,0)->(1,1)
            i1 = 1;
            j1 = 0;
        } else {// upper triangle, YX order: (0,0)->(0,1)->(1,1)
            i1 = 0;
            j1 = 1;
        }

        // A step of (1,0) in (i,j) means a step of (1-c,-c) in (x,y), and
        // a step of (0,1) in (i,j) means a step of (-c,1-c) in (x,y), where
        // c = (3-sqrt(3))/6

        let x1 = x0 - i1 + g2; //offsets for middle corner in (x,y) unskewed coords
        let y1 = y0 - j1 + g2;
        let x2 = x0 - 1 + 2 * g2;
        let y2 = y0 - 1 + 2 * g2;

        // work out the hashed gradient indices of the three simplex corners
        let ii = i & 255;
        let jj = j & 255;
        let gi0 = this._perm[ii + this._perm[jj]] % 12;
        let gi1 = this._perm[ii + i1 + this._perm[jj + j1]] % 12;
        let gi2 = this._perm[ii + 1 + this._perm[jj + 1]] % 12;

        //calculate the contribution from the three corners
        let t0 = 0.5 - x0 * x0 - y0 * y0;
        if (t0 < 0) {
            n0 = 0;
        }
        else {
            t0 *= t0;
            n0 = t0 * t0 * SimplexNoise.dotProduct(this._grad3[gi0][0], this._grad3[gi0][1], x0, y0);// (x,y) of grad3 used for 2D gradient
        }

        let t1 = 0.5 - x1 * x1 - y1 * y1;
        if (t1 < 0) {
            n1 = 0;
        }
        else {
            t1 *= t1;
            n1 = t1 * t1 * SimplexNoise.dotProduct(this._grad3[gi1][0], this._grad3[gi1][1], x1, y1);
        }

        let t2 = 0.5 - x2 * x2 - y2 * y2;
        if (t2 < 0) {
            n2 = 0;
        } else {
            t2 *= t2;
            n2 = t2 * t2 * SimplexNoise.dotProduct(this._grad3[gi2][0], this._grad3[gi2][1], x2, y2);
        }

        // Add contributions from each corner to get the final noise value.
        // The result is scaled to return values in the interval [-1,1].
        return 70.0 * (n0 + n1 + n2);
    }

    static dotProduct(x0, y0, x1, y1) {
        return x0 * x1 + y0 * y1;
    }
}